import { type FormEvent, useCallback, useEffect, useState } from "react";
import type {
  Api,
  Conversation,
  ConversationMessage,
  ConversationStatus,
} from "../api";
import { leadSourceLabel, leadTimeLabel } from "./Leads";

const STATUSES: ConversationStatus[] = [
  "open",
  "needs_owner",
  "closed",
  "spam",
];

export function snippet(text: string, length: number): string {
  if (text.length <= length) return text;
  return `${text.slice(0, Math.max(0, length - 1)).trimEnd()}…`;
}

export function ConversationsCard({ api }: { api: Api }) {
  const [conversations, setConversations] = useState<Conversation[] | null>(
    null,
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    Record<string, ConversationMessage[]>
  >({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [aiMode, setAiMode] = useState<
    "off" | "when_away" | "always_first" | null
  >(null);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [savingLeadId, setSavingLeadId] = useState<string | null>(null);

  const saveAsLead = async (id: string) => {
    setSavingLeadId(id);
    try {
      const result = await api.saveConversationAsLead(id);
      setConversations((current) =>
        (current ?? []).map((conversation) =>
          conversation.id === id
            ? { ...conversation, lead_id: result.leadId }
            : conversation,
        ),
      );
    } catch {
      // surfaced by the unchanged button state; retry is safe (idempotent)
    } finally {
      setSavingLeadId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .chatAiMode()
      .then((result) => {
        if (cancelled) return;
        setAiMode(result.mode);
        setAiAvailable(result.available);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);
  const [savingId, setSavingId] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const result = await api.conversations();
      setConversations(result.conversations);
    } catch {
      // Polling errors are non-fatal; keep the last successful list visible.
    }
  }, [api]);

  const loadMessages = useCallback(
    async (id: string) => {
      try {
        const result = await api.conversationMessages(id);
        setMessages((current) => ({ ...current, [id]: result.messages }));
      } catch {
        // Polling errors are non-fatal; keep the last successful thread visible.
      }
    },
    [api],
  );

  useEffect(() => {
    void loadConversations();
    const interval = window.setInterval(loadConversations, 15_000);
    return () => window.clearInterval(interval);
  }, [loadConversations]);

  useEffect(() => {
    if (!expandedId) return;
    if (!messages[expandedId]) void loadMessages(expandedId);
    const interval = window.setInterval(
      () => void loadMessages(expandedId),
      5_000,
    );
    return () => window.clearInterval(interval);
  }, [expandedId, loadMessages, messages]);

  const updateStatus = async (
    conversation: Conversation,
    status: ConversationStatus,
  ) => {
    if (status === conversation.status) return;
    setSavingId(conversation.id);
    try {
      const { updated } = await api.updateConversation(conversation.id, {
        status,
      });
      setConversations(
        (current) =>
          current?.map((item) => (item.id === updated.id ? updated : item)) ??
          current,
      );
    } catch {
      // Keep the server-confirmed status visible.
    } finally {
      setSavingId(null);
    }
  };

  const sendReply = async (event: FormEvent, id: string) => {
    event.preventDefault();
    const message = (drafts[id] ?? "").trim();
    if (!message || sendingId) return;
    setSendingId(id);
    try {
      const { sent } = await api.replyToConversation(id, message);
      setMessages((current) => ({
        ...current,
        [id]: [...(current[id] ?? []), sent],
      }));
      setDrafts((current) => ({ ...current, [id]: "" }));
      await loadMessages(id);
      await loadConversations();
    } catch {
      // Leave the draft in place so it can be sent again.
    } finally {
      setSendingId(null);
    }
  };

  return (
    <section className="card conversations-card">
      <div className="card-head">
        <div>
          <h2>Conversations</h2>
          <p className="muted">
            Live chat from your site — reply here.
            {(conversations?.filter(
              (conversation) =>
                conversation.status === "needs_owner" ||
                (conversation.status === "open" &&
                  conversation.last_author === "visitor"),
            ).length ?? 0) > 0 && (
              <strong className="conversations-waiting-count">
                {" "}
                {
                  conversations?.filter(
                    (conversation) =>
                      conversation.status === "needs_owner" ||
                      (conversation.status === "open" &&
                        conversation.last_author === "visitor"),
                  ).length
                }{" "}
                waiting on you
              </strong>
            )}
          </p>
        </div>
        <label className="conversations-ai-mode">
          <span>AI assistant</span>
          <select
            aria-label="AI assistant mode"
            disabled={aiMode === null || (aiMode !== "off" && !aiAvailable)}
            onChange={(event) => {
              const mode = event.target.value as
                | "off"
                | "when_away"
                | "always_first";
              setAiMode(mode);
              api.setChatAiMode(mode).catch(() => {
                setAiError("Could not update the assistant setting.");
              });
            }}
            value={aiMode ?? "off"}
          >
            <option value="off">Off</option>
            <option disabled={!aiAvailable} value="when_away">
              Answers when you're away
            </option>
            <option disabled={!aiAvailable} value="always_first">
              Always answers first
            </option>
          </select>
        </label>
      </div>
      {aiError && <p className="error small">{aiError}</p>}

      {conversations?.length === 0 && (
        <p className="muted conversations-empty">
          No conversations yet. Chat appears here as soon as a visitor writes.
        </p>
      )}

      {(conversations?.length ?? 0) > 0 && (
        <ol className="conversations-list">
          {conversations?.map((conversation) => {
            const expanded = conversation.id === expandedId;
            const source = leadSourceLabel(
              conversation.click_id_type,
              conversation.utm_source,
            );
            return (
              <li className="conversations-item" key={conversation.id}>
                <div className="conversations-row">
                  <button
                    aria-expanded={expanded}
                    className="conversations-summary"
                    onClick={() =>
                      setExpandedId(expanded ? null : conversation.id)
                    }
                    type="button"
                  >
                    <span className="conversations-snippet">
                      {snippet(conversation.last_message, 80)}
                    </span>
                    <time className="muted small">
                      {leadTimeLabel(conversation.updated_at, Date.now())}
                    </time>
                  </button>
                  <div className="conversations-meta">
                    {(conversation.click_id_type ||
                      conversation.utm_source) && (
                      <span className="lead-chip lead-chip--paid">
                        via <b>{source}</b>
                      </span>
                    )}
                    <label>
                      <span className="visually-hidden">Status</span>
                      <select
                        aria-label="Conversation status"
                        className={`lead-status__select lead-status__select--${conversation.status}`}
                        disabled={savingId === conversation.id}
                        onChange={(event) =>
                          void updateStatus(
                            conversation,
                            event.target.value as ConversationStatus,
                          )
                        }
                        value={conversation.status}
                      >
                        {STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                {expanded && (
                  <div className="conversations-thread">
                    <div className="conversations-thread-actions">
                      {conversation.lead_id ? (
                        <span className="muted small">
                          Saved as a lead — see the Leads card.
                        </span>
                      ) : (
                        <button
                          className="btn-secondary conversations-save-lead"
                          disabled={savingLeadId === conversation.id}
                          onClick={() => void saveAsLead(conversation.id)}
                          type="button"
                        >
                          {savingLeadId === conversation.id
                            ? "Saving…"
                            : "Save as lead"}
                        </button>
                      )}
                      {conversation.status === "needs_owner" && (
                        <span className="conversations-needs-owner">
                          Needs your attention
                        </span>
                      )}
                    </div>
                    {!messages[conversation.id] && (
                      <p className="muted small">Loading conversation…</p>
                    )}
                    {messages[conversation.id]?.map((message) => (
                      <div
                        className={`conversations-message conversations-message--${message.author}`}
                        key={message.id}
                      >
                        {message.author !== "visitor" && (
                          <span className="conversations-author">
                            {message.author === "owner" ? "You" : "Assistant"}
                          </span>
                        )}
                        <p>{message.body}</p>
                      </div>
                    ))}
                    <form
                      className="conversations-reply"
                      onSubmit={(event) =>
                        void sendReply(event, conversation.id)
                      }
                    >
                      <input
                        aria-label="Reply"
                        maxLength={4000}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [conversation.id]: event.target.value,
                          }))
                        }
                        placeholder="Write a reply…"
                        value={drafts[conversation.id] ?? ""}
                      />
                      <button
                        className="btn-primary"
                        disabled={
                          sendingId === conversation.id ||
                          !(drafts[conversation.id] ?? "").trim()
                        }
                        type="submit"
                      >
                        Send
                      </button>
                    </form>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
